import { ComponentProps, useContext } from "react";
import { getSessionStore } from "app/utils/sessionStore";
import { ProposalTypes } from "app/utils/types";
import { ThemeContext } from "app/uicomponents/macrocomponents/RootLayout";
import { AnimatedCheckbox } from "app/uicomponents/microcomponents/AnimatedChecbox";
import ContactsIcon from "app/assets/IconComponent/ContactsIcon";
import PlanPhasesCard from "./PlanPhasesCard";

type Contact = {
  id?: string;
  user?: { profile?: { firstName?: string; lastName?: string } };
};

interface PlanOverviewSectionProps {
  planPhasesProps?: ComponentProps<typeof PlanPhasesCard>;
  // Buy Sell -> shareholders, Key Person -> employees. Both share `{ user.profile }`.
  contacts?: Contact[];
  // Selected contact keys (controlled). When omitted, every contact reads as checked.
  selectedIds?: string[];
  // Toggle handler — receives the same key used for the checkbox.
  onToggle?: (id: string) => void;
}

const fullName = (c: Contact) =>
  `${c.user?.profile?.firstName ?? ""} ${c.user?.profile?.lastName ?? ""}`.trim();

const PlanOverviewSection = ({
  planPhasesProps,
  contacts = [],
  selectedIds,
  onToggle,
}: PlanOverviewSectionProps) => {
  const sessionPlanType = getSessionStore("planInfo")?.planType;
  const { isDarkMode } = useContext(ThemeContext);
  const isDarkTheme = isDarkMode;

  const isBuySell = sessionPlanType === ProposalTypes.BuySell;
  const isKeyPerson = sessionPlanType === ProposalTypes.KeyPersonInsurance;

  // Plan types without a people side card just render the plan/phases card.
  if (!isBuySell && !isKeyPerson) {
    return <PlanPhasesCard {...(planPhasesProps ?? {})} />;
  }

  const peopleTitle = isKeyPerson ? "Key Persons" : "Shareholders";

  return (
    <div className="flex gap-4 w-full mt-4">
      <PlanPhasesCard {...(planPhasesProps ?? {})} />

      {/* People card — Shareholders (Buy Sell) / Key Persons (Key Person) */}
      <div
        className={`w-1/2 rounded-2xl overflow-hidden shadow-lg ${
          isDarkTheme ? "!bg-[#2A2A3D]" : "!bg-white"
        }`}
      >
        <p
          className={`text-[13px] p-4 font-semibold conthrax-font flex items-center gap-2 mb-3 ${
            isDarkTheme ? "!bg-[#1E1E2E] text-[#93C5FD]" : "!bg-[#F5F5F5] text-[#1E3A8A]"
          }`}
        >
          <ContactsIcon fill={isDarkTheme ? "#93C5FD" : "#1E3A8A"} width="21px" height="21px" />
          {peopleTitle}
        </p>
        <div className="flex flex-wrap gap-2 px-4 pb-4 max-h-[200px] overflow-y-auto">
          {contacts.length > 0 ? (
            contacts.map((c, i) => {
              const key = c.id ?? String(i);
              const checked = selectedIds ? selectedIds.includes(key) : true;
              return (
                <AnimatedCheckbox
                  key={key}
                  checked={checked}
                  onChange={() => onToggle?.(key)}
                  inputId={key}
                  label={fullName(c)}
                  brandingColor="#004781"
                  size={20}
                />
              );
            })
          ) : (
            <p className="text-[15px] text-gray-400">No {peopleTitle.toLowerCase()} available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanOverviewSection;
